import React from 'react';
import { Shield, AlertTriangle, Heart, Eye, UserX, Phone, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Section = ({ id, title, icon, children, tone = 'default' }) => {
  const toneClasses = {
    default: 'bg-white border-gray-200',
    warning: 'bg-amber-50 border-amber-200',
    danger: 'bg-rose-50 border-rose-200',
  };
  return (
    <section id={id} className={`rounded-2xl border ${toneClasses[tone]} p-6 sm:p-8 shadow-sm`}>
      <h2 className="flex items-center gap-3 text-xl sm:text-2xl font-bold text-slate-900 mb-4">
        {icon}
        <span>{title}</span>
      </h2>
      <div className="text-slate-700 text-sm sm:text-base leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
};

const Rule = ({ children }) => (
  <li className="flex items-start gap-2">
    <span className="text-rose-500 font-bold mt-1">•</span>
    <span>{children}</span>
  </li>
);

const CommunityGuidelines = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-3xl mx-auto px-4 py-10 sm:py-14">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-rose-500 transition-colors mb-6"
          data-testid="community-guidelines-back"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <header className="mb-10 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-rose-500/10 border border-rose-200 mb-5">
            <Heart className="h-7 w-7 text-rose-500" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">Community Guidelines</h1>
          <p className="text-slate-500 text-sm sm:text-base max-w-xl mx-auto">
            Shot Clock Dating is a space for adults to meet, talk, and build real connections through
            timed video dates. Breaking these rules can lead to warnings, content removal, or
            permanent account termination.
          </p>
        </header>

        {/* The three rules */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          {[
            { emoji: '🪪', title: 'Be real', text: 'Be the person you say you are.' },
            { emoji: '💛', title: 'Be kind', text: 'Treat everyone with respect.' },
            { emoji: '🛡️', title: 'Be safe', text: 'Keep yourself and others out of harm\u2019s way.' },
          ].map((r) => (
            <div key={r.title} className="rounded-2xl bg-white border border-slate-200 p-5 text-center">
              <div className="text-3xl mb-2">{r.emoji}</div>
              <div className="font-bold text-slate-900 mb-1">{r.title}</div>
              <div className="text-xs text-slate-500">{r.text}</div>
            </div>
          ))}
        </div>

        <div className="space-y-6">
          <Section id="eligibility" title="1. Who Can Use Shot Clock" icon={<Shield className="h-6 w-6 text-rose-500" />}>
            <ul className="space-y-2">
              <Rule>You must be <strong>18 years or older</strong>. No exceptions.</Rule>
              <Rule>One person, one account.</Rule>
              <Rule>No convicted sex offenders.</Rule>
              <Rule>No one previously banned from Shot Clock.</Rule>
            </ul>
            <p>If you see someone who looks underage, report them immediately.</p>
          </Section>

          <Section id="profile" title="2. Your Profile" icon={<Eye className="h-6 w-6 text-rose-500" />}>
            <h3 className="font-semibold text-slate-900">Photos</h3>
            <ul className="space-y-2">
              <Rule><strong>Be yourself.</strong> Your main photo must be a clear, recent photo of you — face visible, not heavily filtered.</Rule>
              <Rule><strong>No one else&rsquo;s photos.</strong> Celebrities, stock photos, cartoons, or someone from the internet all count as catfishing.</Rule>
              <Rule><strong>No children.</strong> Photos containing minors are not allowed on your dating profile.</Rule>
              <Rule><strong>No nudity or sexual content.</strong> No underwear, no suggestive poses, no towel shots.</Rule>
              <Rule>No weapons, drugs, or illegal activity.</Rule>
              <Rule>No photos of recently-deceased people, graves, or funerals.</Rule>
              <Rule>No commercial content, product shots, or logos.</Rule>
            </ul>
            <h3 className="font-semibold text-slate-900 mt-4">Bio &amp; Prompts</h3>
            <ul className="space-y-2">
              <Rule>Be honest about your age, height, job, and relationship status.</Rule>
              <Rule>No external links, usernames from other apps, phone numbers, or email addresses.</Rule>
              <Rule>No political campaigning or recruitment.</Rule>
              <Rule>No racist, sexist, homophobic, transphobic, ableist, or hateful language — even &ldquo;jokes.&rdquo;</Rule>
            </ul>
          </Section>

          <Section id="behavior" title="3. Speed-Date &amp; Chat Behavior" icon={<Heart className="h-6 w-6 text-rose-500" />}>
            <p className="font-semibold text-slate-900">Not allowed:</p>
            <ul className="space-y-2">
              <Rule><strong>Harassment</strong> — repeated unwanted messages, insults, slurs, threats.</Rule>
              <Rule><strong>Sexual messages or content without consent</strong>, including unsolicited explicit photos.</Rule>
              <Rule><strong>Hate speech</strong> — attacks based on race, ethnicity, religion, national origin, sexual orientation, gender identity, disability, or serious disease.</Rule>
              <Rule><strong>Bullying or shaming</strong> — mocking appearance, income, education, body, or voice.</Rule>
              <Rule><strong>Pressure</strong> to meet in person, send photos, or share personal info.</Rule>
              <Rule><strong>Blackmail</strong> or threats to share private content.</Rule>
            </ul>
            <p>
              If a conversation makes you uncomfortable, tap <strong>&ldquo;...&rdquo; → Block</strong> or
              <strong> Report</strong> from any message screen. Our moderation team reviews reports
              within <strong>24 hours</strong>.
            </p>
          </Section>

          <Section
            id="zero-tolerance"
            title="4. Zero Tolerance — Immediate Permanent Ban"
            icon={<UserX className="h-6 w-6 text-rose-600" />}
            tone="danger"
          >
            <p>These behaviors result in permanent account termination on first offense and may be reported to law enforcement:</p>
            <ul className="space-y-2">
              <Rule>Sexual content involving minors (CSAM) — reported to NCMEC</Rule>
              <Rule>Threats of physical or sexual violence</Rule>
              <Rule>Hate groups and terrorism — membership, recruitment, or glorification</Rule>
              <Rule>Human trafficking or non-consensual exploitation</Rule>
              <Rule>Doxxing — sharing someone&rsquo;s private information with intent to harm</Rule>
              <Rule>Non-consensual intimate imagery (&ldquo;revenge porn&rdquo;)</Rule>
              <Rule>Impersonating a real person</Rule>
              <Rule>Scams, fraud, or financial exploitation</Rule>
              <Rule>Promoting self-harm or suicide</Rule>
            </ul>
          </Section>

          <Section
            id="commercial"
            title="5. No Commercial Use"
            icon={<AlertTriangle className="h-6 w-6 text-amber-500" />}
            tone="warning"
          >
            <p>Shot Clock is for personal dating. You may <strong>not</strong> use it to:</p>
            <ul className="space-y-2">
              <Rule>Promote OnlyFans, webcam sites, or sex work</Rule>
              <Rule>Sell products, services, or NFTs</Rule>
              <Rule>Recruit for MLM / network marketing</Rule>
              <Rule>Promote cryptocurrency, trading signals, or investment schemes</Rule>
              <Rule>Fundraise or solicit donations</Rule>
              <Rule>Promote political candidates or campaigns</Rule>
              <Rule>Drive traffic to other social accounts</Rule>
            </ul>
            <p>Commercial accounts are banned on first offense.</p>
          </Section>

          <Section id="safety" title="6. Safety — Online &amp; Offline" icon={<Phone className="h-6 w-6 text-rose-500" />}>
            <h3 className="font-semibold text-slate-900">Protect yourself</h3>
            <ul className="space-y-2">
              <Rule><strong>Keep conversations on Shot Clock</strong> until you trust someone.</Rule>
              <Rule><strong>Never send money, cryptocurrency, or gift cards</strong> to anyone you met here. If someone asks, it&rsquo;s a scam — report them.</Rule>
              <Rule><strong>Don&rsquo;t share</strong> your home address, workplace, bank details, passwords, or government IDs.</Rule>
              <Rule><strong>Reverse-image search</strong> their photos — stolen profiles come up fast.</Rule>
            </ul>
            <h3 className="font-semibold text-slate-900 mt-4">Meeting in person</h3>
            <ul className="space-y-2">
              <Rule><strong>Meet in a public place</strong> for the first few dates.</Rule>
              <Rule><strong>Tell a friend</strong> where you&rsquo;re going, who you&rsquo;re meeting, and when you expect to be back.</Rule>
              <Rule><strong>Get there on your own</strong> — don&rsquo;t be picked up or dropped off.</Rule>
              <Rule><strong>Stay sober enough</strong> to make good decisions.</Rule>
              <Rule><strong>Trust your gut.</strong> If something feels off, leave. You don&rsquo;t owe an explanation.</Rule>
            </ul>
            <h3 className="font-semibold text-slate-900 mt-4">If something goes wrong</h3>
            <ul className="space-y-2">
              <Rule><strong>Immediate danger:</strong> call 911 (US) or your local emergency number.</Rule>
              <Rule><strong>Sexual assault:</strong> call <a className="text-rose-500 underline" href="https://rainn.org">RAINN (1-800-656-HOPE)</a>.</Rule>
              <Rule><strong>Scams:</strong> report to <a className="text-rose-500 underline" href="https://ic3.gov">ic3.gov</a> and your local police.</Rule>
              <Rule>Then email <a className="text-rose-500 underline" href="mailto:safety@shotclockdating.com">safety@shotclockdating.com</a> so we can take action.</Rule>
            </ul>
          </Section>

          <Section id="enforcement" title="7. How We Enforce These Guidelines" icon={<Shield className="h-6 w-6 text-rose-500" />}>
            <ol className="space-y-2 list-decimal list-inside">
              <li><strong>Report submitted</strong> by a user, automated systems, or our team</li>
              <li><strong>Review within 24 hours</strong> by a trained moderator</li>
              <li><strong>Action taken</strong>: warning, content removal, feature restrictions, suspension, or permanent ban</li>
              <li><strong>Both parties notified</strong> where appropriate</li>
            </ol>
            <p>
              Appeal within 14 days: <a className="text-rose-500 underline" href="mailto:appeals@shotclockdating.com">appeals@shotclockdating.com</a>.
              Transparency Report published quarterly.
            </p>
          </Section>

          <Section id="reporting" title="8. Reporting Someone" icon={<AlertTriangle className="h-6 w-6 text-rose-500" />}>
            <ol className="space-y-2 list-decimal list-inside">
              <li>Tap the <strong>&ldquo;...&rdquo; menu</strong> on any profile or message thread</li>
              <li>Choose <strong>Report</strong> or <strong>Block</strong></li>
              <li>Select a reason (harassment, inappropriate photos, scam, fake profile, underage)</li>
              <li>Add helpful detail — screenshots, context, times</li>
              <li>Submit</li>
            </ol>
            <p>
              <strong>Blocking</strong> immediately hides the user in both directions.
              <strong> Reporting</strong> sends the case to our moderation team.
            </p>
            <p className="text-slate-500 text-xs italic">
              You will never be penalized for making a good-faith report, even if we determine no
              violation occurred.
            </p>
          </Section>
        </div>

        <footer className="mt-12 pt-8 border-t border-slate-200 text-center">
          <p className="text-sm text-slate-600 mb-2">Questions?</p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm">
            <a className="text-rose-500 hover:underline" href="mailto:support@shotclockdating.com">Support</a>
            <a className="text-rose-500 hover:underline" href="mailto:safety@shotclockdating.com">Safety</a>
            <a className="text-rose-500 hover:underline" href="mailto:appeals@shotclockdating.com">Appeals</a>
            <a className="text-rose-500 hover:underline" href="mailto:legal@shotclockdating.com">Legal</a>
          </div>
          <p className="text-xs text-slate-400 mt-6">
            Thanks for helping keep Shot Clock Dating a welcoming, safe, and real place to meet someone great.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default CommunityGuidelines;
